import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { combineLatest } from 'rxjs'
import { NavigationService } from '@modules/navigation/services'
import { AccountService } from '@services/account.service'
import { AccountProductType } from '@models/account.model'
import { Router } from '@angular/router'
import { ChartUtil } from '@utils/chart.util'
import { PlatformService } from '@services/platform.service'
import { BOTTOM_NAV_ITEMS } from '@modules/navigation/components/bottom-nav/bottom-nav.component'

@Component({
	selector: 'sb-top-nav',
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: './top-nav.component.html',
	styleUrls: ['top-nav.component.scss'],
	standalone: false
})
export class TopNavComponent implements OnInit {
	@Input() multitenant = false

	protected readonly ProductType = AccountProductType
	private readonly themeStorageKey = 'silgov_theme'
	isDark = true
	/** Modo app (spec 058): cabeçalho compacto, só com o título da tela e o avatar. */
	appNative = false
	pageTitle = ''

	private readonly destroyRef = inject(DestroyRef)
	private readonly cdr = inject(ChangeDetectorRef)

	constructor(
		private router: Router,
		public accountService: AccountService,
		private navigationService: NavigationService,
		public platformService: PlatformService
	) {}

	ngOnInit() {
		this.syncTheme()

		this.platformService.mobile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(mobile => {
			this.appNative = mobile

			if (mobile) {
				this.applyTheme('dark')
			}

			this.cdr.markForCheck()
		})

		combineLatest([this.navigationService.routeData$(), this.navigationService.currentURL$()])
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(([data, url]) => {
				// No app o título é o da aba ("Assinaturas", e não "Assinaturas Eletrônicas"), como num app nativo.
				const tab = BOTTOM_NAV_ITEMS.find(item => (url || this.router.url).startsWith(item.activePrefix))
				this.pageTitle = tab?.label ?? data?.title ?? 'SilGov'
				this.cdr.markForCheck()
			})
	}

	sideNav() {
		this.navigationService.toggleSideNav()
	}

	get linkBrand() {
		if (this.accountService.product.type === AccountProductType.SUP) {
			return '/supplier/board'
		}

		if (this.accountService.product.type === AccountProductType.VIS) {
			return '/visitor/board'
		}

		return '/dashboard'
	}

	async supplierBoard() {
		this.accountService.changeProduct(AccountProductType.SUP)
		await this.router.navigate([`/supplier/board`])
	}

	toggleTheme() {
		if (this.platformService.mobile) {
			return
		}

		this.applyTheme(this.isDark ? 'light' : 'dark')
	}

	private syncTheme() {
		if (this.platformService.mobile) {
			this.applyTheme('dark')
			return
		}

		const storedTheme = localStorage.getItem(this.themeStorageKey)
		if (storedTheme === 'light' || storedTheme === 'dark') {
			this.applyTheme(storedTheme)
			return
		}
		this.applyTheme('dark')
	}

	private applyTheme(theme: 'light' | 'dark') {
		this.isDark = theme === 'dark'
		document.body.setAttribute('data-bs-theme', theme)
		localStorage.setItem(this.themeStorageKey, theme)
		ChartUtil.applyThemeDefaults(theme)
	}
}
